
































  import { defineComponent, ref, onMounted, computed } from 'vue'
  import useCurrentInstance from '~/utils/useCurrentInstance'

  export default defineComponent({
    name: 'OneShopForm',
    components: {
      OneTaskForm: () => import('~/components/molecules/forms/OneTaskForm.vue'),
      FileUpload: () => import('~/components/organisms/shared/FileUpload.vue'),
    },
    props: {
      categoryId: {
        type: Number,
        default: null,
      },
      alt: {
        type: String,
        default: '',
      },
      id: {
        type: String,
        default: 'one-shop-form',
      },
    },
    setup(props: { categoryId: number; id: string }) {
      const vm = useCurrentInstance()

      const isLoaded = ref(false)
      const isSubmitted = ref(false)
      const isFormNotAvailable = ref(false)
      const shopForm = ref<any>({})
      const shopFormRef = ref()
      const initialFormData = ref({})
      const filesToUpload = ref<Record<string, File> | File | null>(null)

      const isFileRequired = computed(() => {
        const fields = shopForm.value?.fields

        if (!fields) return false

        const fileField = fields.single_file || fields.multiple_file

        return !!fileField?.rules?.required
      })

      const isSubmitDisabled = computed(() => {
        if (!isFileRequired.value) return false

        const files = filesToUpload.value

        if (!files) return true

        if (files instanceof File) return false

        if (typeof files === 'object') {
          return Object.keys(files).length === 0
        }

        return true
      })

      const maxAllowedFiles = computed(() => {
        const fields = shopForm.value?.fields;

        return fields?.multiple_files?.rules?.maxFiles ?? 1;
      });

      const setAttachments = (files) => {
        filesToUpload.value = files
      }

      const getAdditionalFields = async () => {
        return await vm.$api.tasks.app
          .getFormByCategoryId(props.categoryId)
          .then(({ data }: any) => {
            shopForm.value = data.schema
            initialFormData.value = JSON.parse(JSON.stringify(data.schema))
          })
          .catch((err: any) => {
            isFormNotAvailable.value = true
          })
          .finally(() => {
            isLoaded.value = true
          })
      }

      const submitForm = async () => {
        const isValid = await shopFormRef.value.validate()
        if (!isValid) return
        let token = ''
        if (vm.$recaptcha) {
          try {
            token = await vm.$recaptcha('shopForm')
            if (!token) return
          } catch (error) {
            console.log('Recaptcha error:', error)
            return
          }
        }

        if(shopForm.value.fields.hasOwnProperty('multiple_files') && filesToUpload.value && !(filesToUpload.value instanceof File) && Object.keys(filesToUpload.value).length > 1) {
          shopForm.value.fields.multiple_files.model = Object.values(filesToUpload.value).map(file => {
            return {
              fileName: file.name,
            }
          })
        } else if (shopForm.value.fields.hasOwnProperty('single_file') && filesToUpload.value && !(filesToUpload.value instanceof File)) {
          shopForm.value.fields.single_file.model.fileName = filesToUpload.value[0].name
        }

        vm.$api.tasks.app
          .submitForm(
            {
              categoryId: props.categoryId,
              content: shopForm.value as any,
            },
            token,
          )
          .then(({ data }) => {
            const files = filesToUpload.value
            if (files) {
              data.uploads.forEach((item, index) => {
                uploadFiles(item.uploadUrl, item.notifyOnUploadFinished, files instanceof File ? files : files[index])
              })
            }
            isSubmitted.value = true
            vm.$notify({
              group: 'alert',
              type: 'success',
              text: vm.$t('form.msg.sent') as string,
            })
          })
          .catch(() => {
            vm.$notify({
              group: 'alert',
              type: 'error',
              text: vm.$t('backend_errors.default') as string,
            })
          })
      }

      const uploadFiles = async (uploadUrl, notifyOnUploadFinished, file) => {
        await vm.$api.comments.app
          .uploadAttachment(uploadUrl, file, file.type)
          .catch((res) => {
            vm.$notify({
              group: 'alert',
              type: 'error',
              title: res?.message?.status || '',
              text: res?.message?.data?.message || (vm.$t('utils.request_failed_txt') as string),
            })
          })
        await vm.$api.comments.app
          .finishUpload(notifyOnUploadFinished)
          .then(() => {
            vm.$notify({
              group: 'alert',
              type: 'success',
              text: vm.$t('attachments.attachment_created') as string,
            })
          })
      }

      const resetForm = () => {
        shopForm.value = JSON.parse(JSON.stringify(initialFormData.value))
        isSubmitted.value = false
        if (shopFormRef.value?.reset) {
          shopFormRef.value.reset()
        }
      }

      getAdditionalFields()

      onMounted(async () => {
        try {
          if(vm.$recaptchaLoaded) {
            await vm.$recaptchaLoaded()
          }
        } catch (e) {
          console.log(e)
        }
      })

      return {
        shopForm,
        shopFormRef,
        isLoaded,
        isSubmitted,
        isFormNotAvailable,
        isFileRequired,
        isSubmitDisabled,
        maxAllowedFiles,
        setAttachments,
        getAdditionalFields,
        submitForm,
        resetForm,
      }
    },
  })
